import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import "./NoteCreator.css";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { encodeTLV, EventKind, NostrPrefix } from "@snort/system";
import { LNURL } from "@snort/shared";
import Icon from "Icons/Icon";
import useEventPublisher from "Feed/EventPublisher";
import { openFile } from "SnortUtils";
import Textarea from "Element/Textarea";
import Modal from "Element/Modal";
import ProfileImage from "Element/ProfileImage";
import useFileUpload from "Upload";
import Note from "Element/Note";
import { setShow, setNote, setError, setActive, setPreview, setShowAdvanced, setSelectedCustomRelays, setZapForward, setSensitive, reset, setPollOptions, setOtherEvents, } from "State/NoteCreator";
import messages from "./messages";
import { useState } from "react";
import Spinner from "Icons/Spinner";
import { Menu, MenuItem } from "@szhsin/react-menu";
import { LoginStore } from "Login";
import { getCurrentSubscription } from "Subscription";
import useLogin from "Hooks/useLogin";
import { System } from "index";
function NotePreview({ note }) {
    return (_jsxs("div", { className: "note-preview", children: [_jsx(ProfileImage, { pubkey: note.pubkey }), _jsxs("div", { className: "note-preview-body", children: [note.content.slice(0, 136), note.content.length > 140 && "..."] })] }));
}
export function NoteCreator() {
    const { formatMessage } = useIntl();
    const publisher = useEventPublisher();
    const uploader = useFileUpload();
    const { note, zapForward, sensitive, pollOptions, replyTo, otherEvents, preview, active, show, showAdvanced, selectedCustomRelays, error, } = useSelector((s) => s.noteCreator);
    const [uploadInProgress, setUploadInProgress] = useState(false);
    const dispatch = useDispatch();
    const sub = getCurrentSubscription(LoginStore.allSubscriptions());
    const login = useLogin();
    const relays = login.relays;
    async function sendNote() {
        if (note && publisher) {
            let extraTags;
            if (zapForward) {
                try {
                    const svc = new LNURL(zapForward);
                    await svc.load();
                    extraTags = [svc.getZapTag()];
                }
                catch {
                    dispatch(setError(formatMessage({ id: "0jOEtS", defaultMessage: "Invalid LNURL" })));
                    return;
                }
            }
            if (sensitive) {
                extraTags ?? (extraTags = []);
                extraTags.push(["content-warning", sensitive]);
            }
            const kind = pollOptions ? EventKind.Polls : EventKind.TextNote;
            if (pollOptions) {
                extraTags ?? (extraTags = []);
                extraTags.push(...pollOptions.map((a, i) => ["poll_option", i.toString(), a]));
            }
            const hk = (eb) => {
                extraTags?.forEach(t => eb.tag(t));
                eb.kind(kind);
                return eb;
            };
            const ev = replyTo ? await publisher.reply(replyTo, note, hk) : await publisher.note(note, hk);
            if (selectedCustomRelays)
                selectedCustomRelays.forEach(r => System.WriteOnceToRelay(r, ev));
            else
                System.BroadcastEvent(ev);
            dispatch(reset());
            for (const oe of otherEvents) {
                if (selectedCustomRelays)
                    selectedCustomRelays.forEach(r => System.WriteOnceToRelay(r, oe));
                else
                    System.BroadcastEvent(oe);
            }
            dispatch(reset());
        }
    }
    async function attachFile() {
        try {
            const file = await openFile();
            if (file) {
                uploadFile(file);
            }
        }
        catch (error) {
            if (error instanceof Error) {
                dispatch(setError(error?.message));
            }
        }
    }
    async function uploadFile(file) {
        setUploadInProgress(true);
        try {
            if (file) {
                const rx = await uploader.upload(file, file.name);
                if (rx.header) {
                    const link = `nostr:${encodeTLV(NostrPrefix.Event, rx.header.id, undefined, rx.header.kind)}`;
                    dispatch(setNote(`${note ? `${note}\n` : ""}${link}`));
                    dispatch(setOtherEvents([...otherEvents, rx.header]));
                }
                else if (rx.url) {
                    dispatch(setNote(`${note ? `${note}\n` : ""}${rx.url}`));
                }
                else if (rx?.error) {
                    dispatch(setError(rx.error));
                }
            }
        }
        catch (error) {
            if (error instanceof Error) {
                dispatch(setError(error?.message));
            }
        }
        finally {
            setUploadInProgress(false);
        }
    }
    function onChange(ev) {
        const { value } = ev.target;
        dispatch(setNote(value));
        if (value) {
            dispatch(setActive(true));
        }
        else {
            dispatch(setActive(false));
        }
    }
    function cancel() {
        dispatch(reset());
    }
    function onSubmit(ev) {
        ev.stopPropagation();
        sendNote().catch(console.warn);
    }
    async function loadPreview() {
        if (preview) {
            dispatch(setPreview(undefined));
        }
        else if (publisher) {
            const tmpNote = await publisher.note(note);
            if (tmpNote) {
                dispatch(setPreview(tmpNote));
            }
        }
    }
    function getPreviewNote() {
        if (preview) {
            return (_jsx(Note, { data: preview, related: [], options: {
                    showFooter: false,
                    canClick: false,
                } }));
        }
    }
    function renderPollOptions() {
        if (pollOptions) {
            return (_jsxs(_Fragment, { children: [_jsx("h4", { children: _jsx(FormattedMessage, { id: 'vhlWFg', defaultMessage: 'Poll Options' }) }), pollOptions?.map((a, i) => (_jsxs("div", { className: "form-group w-max", children: [_jsx("div", { children: _jsx(FormattedMessage, { id: 'mfe8RW', defaultMessage: 'Option: {n}', values: { n: i + 1 } }) }), _jsxs("div", { children: [_jsx("input", { type: "text", value: a, onChange: e => changePollOption(i, e.target.value) }), i > 1 && (_jsx("button", { onClick: () => removePollOption(i), className: "ml5", children: _jsx(Icon, { name: "close", size: 14 }) }))] })] }, `po-${i}`))), _jsx("button", { onClick: () => dispatch(setPollOptions([...pollOptions, ""])), children: _jsx(Icon, { name: "plus", size: 14 }) })] }));
        }
    }
    function changePollOption(i, v) {
        if (pollOptions) {
            const copy = [...pollOptions];
            copy[i] = v;
            dispatch(setPollOptions(copy));
        }
    }
    function removePollOption(i) {
        if (pollOptions) {
            const copy = [...pollOptions];
            copy.splice(i, 1);
            dispatch(setPollOptions(copy));
        }
    }
    function renderRelayCustomisation() {
        return (_jsx("div", { children: Object.keys(relays.item || {})
                .filter(el => relays.item[el].write)
                .map((r, i, a) => (_jsxs("div", { className: "card flex", children: [_jsx("div", { className: "flex f-col f-grow", children: _jsx("div", { children: r }) }), _jsx("div", { children: _jsx("input", { type: "checkbox", checked: !selectedCustomRelays || selectedCustomRelays.includes(r), onChange: e => dispatch(setSelectedCustomRelays(
                            // set false if all relays selected
                            e.target.checked && selectedCustomRelays && selectedCustomRelays.length == a.length - 1
                                ? false
                                : // otherwise return selectedCustomRelays with target relay added / removed
                                    a.filter(el => el === r ? e.target.checked : !selectedCustomRelays || selectedCustomRelays.includes(el)))) }) })] }))) }));
    }
    function listAccounts() {
        return LoginStore.getSessions().map(a => (_jsx(MenuItem, { onClick: ev => {
                ev.stopPropagation = true;
                LoginStore.switchAccount(a);
            }, children: _jsx(ProfileImage, { pubkey: a, link: "" }) })));
    }
    const handlePaste = evt => {
        if (evt.clipboardData) {
            const clipboardItems = evt.clipboardData.items;
            const items = Array.from(clipboardItems).filter(function (item) {
                // Filter the image items only
                return /^image\//.test(item.type);
            });
            if (items.length === 0) {
                return;
            }
            const item = items[0];
            const blob = item.getAsFile();
            if (blob) {
                uploadFile(blob);
            }
        }
    };
    return (_jsx(_Fragment, { children: show && (_jsxs(Modal, { className: "note-creator-modal", onClose: () => dispatch(setShow(false)), children: [replyTo && _jsx(NotePreview, { note: replyTo }), preview && getPreviewNote(), !preview && (_jsxs("div", { onPaste: handlePaste, className: `flex note-creator${replyTo ? " note-reply" : ""}${pollOptions ? " poll" : ""}`, children: [_jsxs("div", { className: "flex f-col f-grow", children: [_jsx(Textarea, { autoFocus: true, className: `textarea ${active ? "textarea--focused" : ""}`, onChange: onChange, value: note, onFocus: () => dispatch(setActive(true)), onKeyDown: e => {
                                        if (e.key === "Enter" && e.metaKey) {
                                            sendNote().catch(console.warn);
                                        }
                                    } }), renderPollOptions(), _jsxs("div", { className: "insert", children: [sub && (_jsx(Menu, { menuButton: _jsx("button", { children: _jsx(Icon, { name: "code-circle" }) }), menuClassName: "ctx-menu", children: listAccounts() })), pollOptions === undefined && !replyTo && (_jsx("button", { onClick: () => dispatch(setPollOptions(["A", "B"])), children: _jsx(Icon, { name: "pie-chart" }) })), _jsx("button", { onClick: attachFile, children: _jsx(Icon, { name: "attachment" }) })] })] }), error && _jsx("span", { className: "error", children: error })] })), _jsxs("div", { className: "note-creator-actions", children: [uploadInProgress && _jsx(Spinner, {}), _jsx("button", { className: "secondary", onClick: () => dispatch(setShowAdvanced(!showAdvanced)), children: _jsx(FormattedMessage, { id: '3Rx6Qo', defaultMessage: 'Advanced' }) }), _jsx("button", { className: "secondary", onClick: cancel, children: _jsx(FormattedMessage, { ...messages.Cancel }) }), _jsx("button", { onClick: onSubmit, children: replyTo ? _jsx(FormattedMessage, { ...messages.Reply }) : _jsx(FormattedMessage, { ...messages.Send }) })] }), showAdvanced && (_jsxs("div", { children: [_jsx("button", { className: "secondary", onClick: loadPreview, children: _jsx(FormattedMessage, { id: '1iQ8GN', defaultMessage: 'Toggle Preview' }) }), _jsx("h4", { children: _jsx(FormattedMessage, { id: 'EcZF24', defaultMessage: 'Custom Relays' }) }), _jsx("p", { children: _jsx(FormattedMessage, { id: 'th5lxp', defaultMessage: 'Send note to a subset of your write relays' }) }), renderRelayCustomisation(), _jsx("h4", { children: _jsx(FormattedMessage, { id: 'R1fEdZ', defaultMessage: 'Forward Zaps' }) }), _jsx("p", { children: _jsx(FormattedMessage, { id: 'P04gQm', defaultMessage: 'All zaps sent to this note will be received by the following LNURL' }) }), _jsx("b", { className: "warning", children: _jsx(FormattedMessage, { id: 'gXgY3+', defaultMessage: 'Not all clients support this yet' }) }), _jsx("input", { type: "text", className: "w-max", placeholder: formatMessage({ id: "FP+D3H", defaultMessage: "LNURL to forward zaps to" }), value: zapForward, onChange: e => dispatch(setZapForward(e.target.value)) }), _jsx("h4", { children: _jsx(FormattedMessage, { id: 'bQdA2k', defaultMessage: 'Sensitive Content' }) }), _jsx("p", { children: _jsx(FormattedMessage, { id: 'UUPFlt', defaultMessage: 'Users must accept the content warning to show the content of your note.' }) }), _jsx("b", { className: "warning", children: _jsx(FormattedMessage, { id: 'gXgY3+', defaultMessage: 'Not all clients support this yet' }) }), _jsx("div", { className: "flex", children: _jsx("input", { className: "w-max", type: "text", value: sensitive, onChange: e => dispatch(setSensitive(e.target.value)), maxLength: 50, minLength: 1, placeholder: formatMessage({ id: "AkCxS/", defaultMessage: "Reason" }) }) })] }))] })) }));
}
